import { platformInfo } from '@gem/utils';

import Tasks from 'APP/Tasks';
import { GroupType } from 'APP/model/group/groupModel.types';
import { MAX_MESSAGE_LENGTH } from 'APP/model/message/messageModel.constants';
import { MlsStorageService } from 'SERVICES/mlsStorageService/MlsStorageService';
import Entities from 'STORE';
import { E2eePayload } from 'STORE/Messages/Message/Payload/E2eePayload/E2eePayload';
import { TextPayload } from 'STORE/Messages/Message/Payload/TextPayload/TextPayload';
import { splitMessageText } from 'UTILS/message/message';

import { getLinkPreview } from './getLinkPreview';

interface ICreatePayloadData {
  groupId: string;
  text: string;
  currentEpoch: bigint;
}

async function createPayload(data: ICreatePayloadData): Promise<E2eePayload | null> {
  const encodedPayload = await Tasks.e2ee.getE2eePayloadData(data.groupId, data.text);

  if (!encodedPayload) {
    return null;
  }

  return new E2eePayload({
    e2eePayload: encodedPayload.payload,
    selfPayload: encodedPayload.selfPayload,
    epoch: Number(data.currentEpoch),
    senderDeviceId: platformInfo.deviceId,
    groupId: data.groupId,
    text: data.text,
  });
}

export async function createTextMessage(groupId: string): Promise<boolean> {
  const inputPanel = Entities.InputPanelsStore.getById(groupId);
  const group = Entities.GroupsStore.getGroupById(groupId);

  if (!inputPanel || !group) {
    return false;
  }

  if (inputPanel.isEmptyValue) {
    Tasks.messaging.clearInputPanel(groupId);
    return false;
  }

  const text = inputPanel.markdownValue.trim();
  const splittedTexts = splitMessageText(text, MAX_MESSAGE_LENGTH);
  const linkPreview = splittedTexts.length === 1 ? getLinkPreview(groupId) : null;
  const userMentions =
    splittedTexts.length === 1 ? Tasks.messaging.getMentionsDataFromText(text) : [];

  let payloads: TextPayload[] | E2eePayload[] = [];

  const mlsGroup = MlsStorageService.loadGroup(groupId);
  if (group.type !== GroupType.SecretGroup || !mlsGroup) {
    payloads = splittedTexts.map((text) => {
      return new TextPayload({
        text,
        linkPreview,
        linkPreviewCancelled: inputPanel.linkPreviewCancelled,
        userMentions,
      });
    });
  } else {
    const payloadsPromises: Promise<E2eePayload | null>[] = [];
    splittedTexts.forEach((text) => {
      const currentEpoch = mlsGroup.currentEpoch();
      payloadsPromises.push(
        createPayload({
          groupId,
          text,
          currentEpoch,
        })
      );
    });
    const result = await Promise.all(payloadsPromises);

    payloads = result.filter((item) => !!item);
  }

  let sendError = '';

  payloads.some((payload: TextPayload | E2eePayload): boolean => {
    const canSend = Tasks.messaging.canSendMessage({ groupId, payload });
    sendError = !canSend.result ? canSend.error || '' : '';
    return !!sendError;
  });

  if (sendError) {
    Tasks.app.showToast(sendError);
    return false;
  }

  await Tasks.messaging.createNewMessages(
    groupId,
    payloads.map((payload) => ({
      payload,
      repliedMessage: inputPanel.replyMessage || undefined,
      quote: inputPanel.quote || undefined,
    }))
  );

  return true;
}
